import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { PRICING_PLANS } from '@/utils/constants';
import { PricingPlan } from '@/types';
import { cn } from '@/utils/cn';

interface ComparisonTableProps {
  plans?: PricingPlan[];
}

// 按套餐 id 存储每项功能的取值
type FeatureValue = string | boolean;
interface Feature {
  name: string;
  values: Record<string, FeatureValue>;
  highlight?: boolean;
}

export const ComparisonTable: FC<ComparisonTableProps> = ({ plans = PRICING_PLANS }) => {
  const featureCategories: { name: string; features: Feature[] }[] = [
    {
      name: '🚀 核心指标',
      features: [
        {
          name: '每日可用限额',
          values: { 'claude-carpool': '$60', 'claude-exclusive': '不限量', 'claude-package': '按额度' },
          highlight: true
        },
        {
          name: '并发终端数',
          values: { 'claude-carpool': '拼车共享', 'claude-exclusive': '5个终端', 'claude-package': '灵活使用' },
          highlight: true
        },
        {
          name: '上下文窗口',
          values: { 'claude-carpool': '1M tokens', 'claude-exclusive': '1M tokens', 'claude-package': '1 tokens' },
          highlight: false
        },
      ]
    },
    {
      name: '🤖 模型访问',
      features: [
        {
          name: 'Claude全模型',
          values: { 'claude-carpool': true, 'claude-exclusive': true, 'claude-package': true }
        },
        {
          name: 'Claude Opus 4.0 / 4.1',
          values: { 'claude-carpool': true, 'claude-exclusive': true, 'claude-package': true }
        },
        {
          name: '独立整号（非共享）',
          values: { 'claude-carpool': false, 'claude-exclusive': true, 'claude-package': false }
        },
      ]
    },
    {
      name: '⚡ 使用特性',
      features: [
        { name: '每日无限制请求', values: { 'claude-carpool': false, 'claude-exclusive': true, 'claude-package': false } },
        { name: '多终端并发', values: { 'claude-carpool': false, 'claude-exclusive': true, 'claude-package': false } },
        { name: '按量灵活使用', values: { 'claude-carpool': false, 'claude-exclusive': false, 'claude-package': true } },
        { name: '90天长效期', values: { 'claude-carpool': false, 'claude-exclusive': false, 'claude-package': true } },
      ]
    },
    {
      name: '💎 服务保障',
      features: [
        { name: '售后技术群', values: { 'claude-carpool': true, 'claude-exclusive': true, 'claude-package': false } },
        { name: '专属客服一对一', values: { 'claude-carpool': false, 'claude-exclusive': true, 'claude-package': false } },
        { name: '社区支持', values: { 'claude-carpool': false, 'claude-exclusive': false, 'claude-package': true } },
      ]
    }
  ];

  const renderFeatureValue = (value: FeatureValue | undefined, isHighlight?: boolean) => {
    if (value === undefined) {
      return <span className="text-gray-300 text-xl">—</span>;
    }
    if (typeof value === 'boolean') {
      return value ? (
        <span className="text-green-600 text-xl">✓</span>
      ) : (
        <span className="text-gray-400 text-xl">✗</span>
      );
    }
    return (
      <span className={cn(
        "text-sm",
        isHighlight ? "font-bold text-lg" : "text-gray-700"
      )}>
        {value}
      </span>
    );
  };

  // 获取套餐表头样式
  const getPlanHeaderStyle = (planId: string) => {
    switch (planId) {
      case 'claude-carpool':
        return 'bg-gradient-to-br from-blue-100 to-purple-100 border-primary-500';
      case 'claude-exclusive':
        return 'bg-gradient-to-br from-amber-100 to-orange-100 border-amber-500';
      case 'claude-package':
        return 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-500';
      default:
        return 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-400';
    }
  };

  const getPlanCellStyle = (planId: string, isHighlight?: boolean) => {
    if (!isHighlight) return '';

    switch (planId) {
      case 'claude-carpool':
        return 'text-primary-600 bg-blue-50';
      case 'claude-exclusive':
        return 'text-amber-600 bg-amber-50';
      case 'claude-package':
        return 'text-green-700 bg-green-50';
      default:
        return '';
    }
  };

  // 获取计划徽章信息
  const getPlanBadgeInfo = (planId: string) => {
    switch (planId) {
      case 'claude-carpool':
        return { text: '🚗 拼车', className: 'bg-gradient-to-r from-primary-600 to-accent-500' };
      case 'claude-exclusive':
        return { text: '🏅 独享账号', className: 'bg-gradient-to-r from-amber-600 to-orange-600' };
      case 'claude-package':
        return { text: '🎟️ 流量包', className: 'bg-gradient-to-r from-green-600 to-emerald-600' };
      default:
        return null;
    }
  };

  // 获取按钮样式和文本
  const getPlanButtonInfo = (planId: string) => {
    switch (planId) {
      case 'claude-carpool':
        return { text: '立即拼车', className: 'bg-gradient-to-r from-primary-600 to-accent-500 hover:from-primary-700 hover:to-accent-600' };
      case 'claude-exclusive':
        return { text: '立即尊享', className: 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700' };
      case 'claude-package':
        return { text: '立即购买', className: 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' };
      default:
        return { text: '立即选择', className: 'bg-primary-600 hover:bg-primary-700' };
    }
  };

  // 获取价格颜色
  const getPriceColor = (planId: string) => {
    switch (planId) {
      case 'claude-carpool':
        return 'text-primary-600';
      case 'claude-exclusive':
        return 'text-amber-600';
      case 'claude-package':
        return 'text-green-700';
      default:
        return 'text-gray-900';
    }
  };

  return (
    <div className="overflow-x-auto shadow-2xl rounded-xl">
      <table className="w-full min-w-4xl">
        {/* Header */}
        <thead>
          <tr>
            <th className="text-left py-6 px-6 font-semibold text-gray-900 bg-gray-50">功能特性</th>
            {plans.map((plan) => (
              <th key={plan.id} className="text-center py-6 px-6 min-w-64 bg-gray-50">
                <div className={cn(
                  'relative p-4 rounded-xl border-2 shadow-lg',
                  getPlanHeaderStyle(plan.id)
                )}>
                  {(() => {
                    const badgeInfo = getPlanBadgeInfo(plan.id);
                    return badgeInfo && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className={cn(
                          "text-white px-3 py-1 text-xs font-bold rounded-full shadow-lg",
                          badgeInfo.className
                        )}>
                          {badgeInfo.text}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</div>
                  <div className="mb-4">
                    <div className="flex items-baseline justify-center space-x-2">
                      {plan.originalPrice && (
                        <span className="text-sm text-gray-500 line-through">
                          ¥{plan.originalPrice}
                        </span>
                      )}
                      <span className={cn(
                        "text-3xl font-bold",
                        getPriceColor(plan.id)
                      )}>
                        ¥{plan.price}
                      </span>
                      <span className="text-gray-600">{plan.isPackage ? '/3个月' : '/月'}</span>
                    </div>
                    {plan.discount && (
                      <div className="mt-2">
                        <span className="bg-gradient-to-r from-warning-500 to-red-500 text-white px-2 py-1 text-xs font-bold rounded-full">
                          限时优惠 -¥{plan.discount}
                        </span>
                      </div>
                    )}
                  </div>
                  {(() => {
                    const buttonInfo = getPlanButtonInfo(plan.id);
                    return (
                      <Button
                        as={Link}
                        to="/contact"
                        className={cn(
                          "w-full font-bold",
                          buttonInfo.className
                        )}
                        size="sm"
                      >
                        {buttonInfo.text}
                      </Button>
                    );
                  })()}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {featureCategories.map((category) => (
            <>
              <tr key={category.name} className="bg-gradient-to-r from-gray-100 to-gray-50">
                <td colSpan={plans.length + 1} className="py-4 px-6 font-bold text-gray-800 text-lg">
                  {category.name}
                </td>
              </tr>
              {category.features.map((feature, featureIndex) => (
                <tr
                  key={feature.name}
                  className={cn(
                    'border-b border-gray-200 transition-colors hover:bg-gray-50',
                    featureIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                  )}
                >
                  <td className={cn(
                    "py-4 px-6 text-gray-700",
                    feature.highlight && "font-semibold"
                  )}>
                    {feature.name}
                  </td>
                  {plans.map((plan) => (
                    <td
                      key={plan.id}
                      className={cn(
                        "py-4 px-6 text-center",
                        getPlanCellStyle(plan.id, feature.highlight)
                      )}
                    >
                      {renderFeatureValue(feature.values[plan.id], feature.highlight)}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
};
